import AppPanel from '../fw/AppPanel.react';
import React from 'react';
import PropTypes from 'prop-types';
import Relay from 'react-relay';
import DetailToolBar from '../fw/DetailToolBar.react.js';
import DetailBox from '../fw/DetailBox.react.js';
import TableDetail from '../fw/TableDetail.react';
import {wrapNotFound} from '../app/NotFoundHandler.react';

import urlMapping from '../app/URLMapping';
import types from '../../common/model-api/types';
import ChannelIcon from '../channel-defs/ChannelIcon.react';
import { FormattedDate, FormattedMessage, FormattedNumber, FormattedTime, defineMessages, injectIntl , IntlShape} from 'react-intl';
import Link from 'react-router/lib/Link';
import ChannelErrorBox from './ChannelErrorBox.react';

import ConfirmDialog from '../fw/ConfirmDialog.react';
import MarkChannelErrorMutation from './MarkChannelErrorMutation.react';
import LoadingButton from '../fw/LoadingButtonNew.react';
import Grid from '../fw/Grid.react.js';
import {renderTime} from "../flow-invocations/FlowInvocationList.react";
import { User } from '../../common/types/types';

const messages = defineMessages({
    markOkDescription: {id: 'channelErrors.markOneOkDescription', 'defaultMessage': 'Mark this document as ok. It will be shown as resolved until next error occurs (same or different).'},
    markOkButton: {id: 'channelErrors.markOneOkButton', 'defaultMessage': 'Mark ok'},
    ignoreDescription: {id: 'channelErrors.ignoreOneDescription', 'defaultMessage': 'Mark this document as ignored for errors. Errors on this will not be reported in the future and will not be shown in the list of errors. You can still see it in the error detail or filter ignored errors.'},
    ignoreButton: {id: 'channelErrors.ignoreOneButton', 'defaultMessage': 'Mark ignored'},
    ignoreForFutureDescription: {id: 'channelErrors.ignoreForFutureDescription', 'defaultMessage': 'Ignore this record for future processing and ignore all future potential errors for this record. We will not try to transfer whole document again and will skip it in the future.'},
    ignoreForFutureButton: {id: 'channelErrors.ignoreForFutureButton', 'defaultMessage': 'Ignore for future processing'},
    unmarkIgnoreForFuture: {id: 'channelErrors.unmarkIgnoreForFuture', 'defaultMessage': 'Allow future processing'},
    unmarkIgnoreForFutureButton: {id: 'channelErrors.unmarkIgnoreForFutureButton', 'defaultMessage': 'Allow future processing'},
    unmarkIgnoreForFutureDescription: {id: 'channelErrors.unmarkIgnoreForFutureDescription', 'defaultMessage': 'Allow all future processing of this record'},
    channelErrorViewTitle: {id: 'channelErrorView.title', 'defaultMessage': 'Error detail'},
});


interface ChannelErrorViewProps {
 intl: IntlShape;
 relay?: object;
 user: User;
}


class ChannelErrorView extends React.Component<ChannelErrorViewProps> {

    typeName = 'channelErrors';

    static propTypes = {
        intl: PropTypes.object.isRequired,
        relay: PropTypes.object,
        user: PropTypes.object.isRequired
    };

    constructor() {
        super();
    }

    invokeMarkOk() {
        let channelError = this.props.user.organisation.channel.channelError;

        return new Promise((resolve, reject) => {
            let onSuccess = (response) => {
                resolve();
            };

            this.props.relay.commitUpdate(
                new MarkChannelErrorMutation({
                    channelError: channelError,
                    channel: this.props.user.organisation.channel,
                    actionMarkOk: true,
                }), {onSuccess: onSuccess, onFailure: reject}
            );
        });
    }


    onClickMarkOk() {
        return new Promise((resolve, reject) => {
            let confirmDialogConfig  = {
                message: this.props.intl.formatMessage(messages.markOkDescription),
                confirm: this.props.intl.formatMessage(messages.markOkButton),
                confirmStyle: 'btn-danger'
            };

            return ConfirmDialog(this.props.intl, confirmDialogConfig).then(() => {
                return this.invokeMarkOk().then(() => {
                    return resolve();
                });
            }).catch(() => {
                resolve();
            });
        });
    }

    invokeIgnored() {
        let channelError = this.props.user.organisation.channel.channelError;

        return new Promise((resolve, reject) => {
            let onSuccess = (response) => {
                resolve();
            };

            this.props.relay.commitUpdate(
                new MarkChannelErrorMutation({
                    channelError: channelError,
                    channel: this.props.user.organisation.channel,
                    actionMarkOk: true,
                }), {onSuccess: onSuccess, onFailure: reject}
            );
        });
    }


    onClickIgnored() {
        return new Promise((resolve, reject) => {
            let confirmDialogConfig  = {
                message: this.props.intl.formatMessage(messages.ignoreDescription),
                confirm: this.props.intl.formatMessage(messages.ignoreButton),
                confirmStyle: 'btn-danger'
            };

            return ConfirmDialog(this.props.intl, confirmDialogConfig).then(() => {
                return this.invokeMarkOk().then(() => {
                    return resolve();
                });
            }).catch(() => {
                resolve();
            });
        });
    }

    invokeIgnoreForFuture(value) {
        let channelError = this.props.user.organisation.channel.channelError;

        return new Promise((resolve, reject) => {
            let onSuccess = (response) => {
                resolve();
            };

            this.props.relay.commitUpdate(
                new MarkChannelErrorMutation({
                    channelError: channelError,
                    channel: this.props.user.organisation.channel,
                    actionIgnoreForFuture: value,
                }), {onSuccess: onSuccess, onFailure: reject}
            );
        });
    }

    onClickIgnoreForFuture(value = true) {
        return new Promise((resolve, reject) => {
            let confirmDialogConfig  = {
                message: this.props.intl.formatMessage(messages.ignoreForFutureDescription),
                confirm: this.props.intl.formatMessage(messages.ignoreForFutureButton),
                confirmStyle: 'btn-danger'
            };

            if (!value) {
                confirmDialogConfig.message = this.props.intl.formatMessage(messages.unmarkIgnoreForFutureDescription);
                confirmDialogConfig.confirm = this.props.intl.formatMessage(messages.unmarkIgnoreForFutureButton);
                confirmDialogConfig.confirmStyle = 'btn-warning';
            }

            return ConfirmDialog(this.props.intl, confirmDialogConfig).then(() => {
                return this.invokeIgnoreForFuture(value).then(() => {
                    return resolve();
                });
            }).catch(() => {
                resolve();
            });
        });
    }



    render() {
        const data = this.props.user.organisation.channel.channelError;
        let type = types[this.typeName];

        let lastTransferFetches = data.lastTransferFetches?.edges.map((edge) => edge.node) || [];

        return (
            <AppPanel breadcrumbParams={[
                {type, data},
                {type: types['channels'], data: this.props.user.organisation.channel},
                {type: types['organisations'], data: this.props.user.organisation},
            ]} title={this.props.intl.formatMessage(messages.channelErrorViewTitle)}>
                <DetailBox data={data} metadata={type} urlMapping={urlMapping}>
                    <DetailToolBar data={data} metadata={type} urlMapping={urlMapping}>
                        {data.hasError ? <LoadingButton className="btn btn-default" icon="fa-check" key='check' onClick={() => { this.onClickMarkOk(); }} title={this.props.intl.formatMessage(messages.markOkDescription)} text={<FormattedMessage defaultMessage="Mark as ok" id="channelErrors.markOneOk" />} /> : null}
                        {!data.ignoredError && data.hasError ? <LoadingButton className="btn btn-default" icon="fa-exclamation" key='ignored' onClick={() => { this.onClickIgnored(); }} title={this.props.intl.formatMessage(messages.ignoreDescription)} text={<FormattedMessage defaultMessage="Mark as ignored" id="channelErrors.markOneIgnored" />} /> : null}

                        {!data.ignoreForFuture && data.hasError ? <LoadingButton className="btn btn-default" icon="fa-exclamation" key='ignoredForFuture' onClick={() => { this.onClickIgnoreForFuture(true); }} title={this.props.intl.formatMessage(messages.ignoreForFutureDescription)} text={<FormattedMessage defaultMessage="Ignore future processing" id="channelErrors.markIgnoreForFuture" />} /> : <LoadingButton className="btn btn-default" icon="fa-check" key='ignoredForFuture' onClick={() => { this.onClickIgnoreForFuture(false); }} text={<FormattedMessage defaultMessage="Allow future processing" id="channelErrors.unmarkIgnoreForFuture" />} />}

                    </DetailToolBar>

                    <h2>{data?.sourceDef?.name || <FormattedMessage defaultMessage="Document" id="channelError.document" />}: {data.displayName}</h2>
                    {data.hasError ? <ChannelErrorBox channelDef={this.props.user.organisation.channel.channelDef} channelErrorDef={data.channelErrorDef} channelErrorMessages={[data.channelErrorMessage]}>
                        {data['lastErrorTransfer'] ? <p style={{marginTop: '15px', marginBottom: 0}}><Link to={urlMapping.getUrl(types['fetches'], 'view', data['lastErrorTransfer'])}><FormattedMessage defaultMessage="View failed API request..." id="channelErrors.viewFailedAPI" /></Link></p> : null}
                    </ChannelErrorBox> : null}

                    <TableDetail columns={{
                        'channel': {
                            linkToView: true,
                            header: (<span></span>),
                            render: (column) => {
                                if (data.channelDef) {
                                    return <span><ChannelIcon channelDef={data.channelDef} size={32} /> {data.channelDef.name} {data.channel.name ? " - " + data.channel.name : undefined}</span>;
                                }

                                return null;
                            }
                        },
                        'referencedId': {},
                        'objectDate': {
                            render: (column) => {
                                return <FormattedDate value={data[column]}/>;
                            }
                        },
                    }} data={data} metadata={type} urlMapping={urlMapping} />

                    <br/>

                    <TableDetail columns={{
                        'comment': {},
                        'ignoredError': {},
                        'actionIgnoreForFuture': {},
                        'lastError': {
                            linkToView: true,
                        },
                        'transferCount': {
                            render: (column, item) => {
                                return <span>{data[column] || 0}&nbsp;&times;{data['errorCount'] > 0 ? <small style={{color:"red"}}>&nbsp;(<FormattedMessage defaultMessage="Error:" id="channelErrors.error" /> {data['errorCount'] || 0}&nbsp;&times;)</small> : undefined}</span>;
                            }
                        },
                        'firstTransferAt': {
                            render: (column, item) => {
                                let content = <span><FormattedDate value={data[column]}/> <FormattedTime hour='numeric' minute='numeric' second='numeric' value={data[column]}/></span>;
                                if (data['firstTransfer']) {
                                    return <Link to={urlMapping.getUrl(types['fetches'], 'view', data['firstTransfer'])}>{content}</Link>;
                                }

                                return content;
                            }
                        },
                        'lastTransferAt': {
                            render: (column, item) => {
                                let content = <span><FormattedDate value={data[column]}/> <FormattedTime hour='numeric' minute='numeric' second='numeric' value={data[column]}/></span>;
                                if (data['lastTransfer']) {
                                    return <Link to={urlMapping.getUrl(types['fetches'], 'view', data['lastTransfer'])}>{content}</Link>;
                                }

                                return content;
                            }
                        },
                        'lastSuccessTransferAt': {
                            render: (column, item) => {
                                if (!data[column]) {
                                    return;
                                }

                                let content = <span><FormattedDate value={data[column]}/> <FormattedTime hour='numeric' minute='numeric' second='numeric' value={data[column]}/></span>;
                                if (data['lastSuccessTransfer']) {
                                    return <Link to={urlMapping.getUrl(types['fetches'], 'view', data['lastSuccessTransfer'])}>{content}</Link>;
                                }

                                return content;
                            }
                        },
                        'lastErrorTransferAt': {
                            render: (column, item) => {
                                let content = <span><FormattedDate value={data[column]}/> <FormattedTime hour='numeric' minute='numeric' second='numeric' value={data[column]}/></span>;
                                if (data['lastErrorTransfer']) {
                                    return <Link to={urlMapping.getUrl(types['fetches'], 'view', data['lastErrorTransfer'])}>{content}</Link>;
                                }

                                return content;
                            }
                        },
                        'flow.name': {
                            linkToView: true,
                            render: (column, item) => {
                                if (data.flow && data.flow.name) {
                                    return <span>{data.flow.name}</span>;
                                } else if (data.flowDef && data.flowDef.name) {
                                    return <span>{data.flowDef.name}</span>;
                                }

                                return null;
                            },
                        },

                    }} data={data} metadata={type} />

                    <h2><FormattedMessage defaultMessage="Relevant API requests" id="channelErrorView.releventApiRequests" /></h2>
                    <Grid
                        columns={{
                            'channelDef': {
                                header: (<span></span>),
                                render: (column, item) => {
                                    if (item.channelDef) {
                                        return <ChannelIcon channelDef={item.channelDef} size={32} />;
                                    }

                                    return null;
                                }
                            },
                            'createdAt': {
                                render: (column, item) => {
                                    return renderTime(item, column);
                                }
                            },
                            'success': {
                                className: (item) => {
                                    return item.success ? 'successValue' : 'unsuccessValue';
                                },
                                render: (column, item) => {
                                    if (item[column] === true) {
                                        return <FormattedMessage defaultMessage="yes" id="Tool.smallYes" />;
                                    }

                                    return <FormattedMessage defaultMessage="{errorType} - {error}" id="Common.dashJoin" values={{errorType: item.errorType, error: item.error}} />;
                                }
                            },
                            'duration': {
                                render: (column, item) => {
                                    return (
                                        <span style={{width: '40px', textAlign: 'right', display: 'inline-block'}}>
                                            <FormattedNumber value={item[column]} />
                                        </span>
                                    );
                                }
                            },
                            'requestSource': {
                                withoutMapping: true,
                                showAlways: true,
                                header: (
                                    <span>
                                        <FormattedMessage defaultMessage="Request source" id="fetches.requestSource" />
                                    </span>
                                ),
                                render: (column, item) => {
                                    return (
                                        <span style={{textAlign: 'left', display: 'inline-block'}}>
                                            {[
                                                item.actionDef ? item.actionDef.name : '',
                                                item.sourceDef ? item.sourceDef.name : '',
                                                item.flow ? item.flow.name : '',
                                                item.flowDef ? item.flowDef.name : '',
                                                item.flowStepDef ? item.flowStepDef.name : '',
                                                item.name ? item.name : '',
                                            ].filter((a) => !!a).join(" | ")}
                                        </span>
                                    );
                                }
                            }
                        }}
                        getUrl={(row, column) => {
                            if (!row.channel_id) {
                                return urlMapping.getUrl('fetches', 'view2', row);
                            }
                            return urlMapping.getUrl('fetches', 'view', row);
                        }}
                        list={lastTransferFetches}
                        metadata={type}
                        relay={this.props.relay}
                        urlMapping={urlMapping}
                    />

                </DetailBox>
            </AppPanel>
        );
    }
}

export default Relay.createContainer(wrapNotFound(injectIntl(ChannelErrorView), (props) => {
    return props.user.organisation && props.user.organisation.channel && props.user.organisation.channel.channelError;
}), {
    initialVariables: {
        organisationId: '',
        channelId: '',
        channelErrorId: '',
    },
    prepareVariables (variables) {
        return variables;
    },
    fragments: {
        user: () => Relay.QL`
            fragment on User {
                organisation(id: $organisationId) {
                    id,
                    realId,
                    name,
                    channel(id: $channelId) {
                        id,
                        name,
                        channelDef {
                            id,
                            name,
                            icon,
                        },
                        organisation_id,
                        channelError(id: $channelErrorId) {
                            createdAt,
                            type,
                            id,
                            realId,
                            channelErrorMessage,
                            channelErrorField,
                            hasError,
                            ignoredError,
                            ignoreForFuture,
                            channel_id,
                            channel {
                                id,
                                organisation_id,
                                name,
                            },
                            channelDef {
                                id,
                                name,
                                icon,
                            },
                            sourceDef {
                                id,
                                name,
                            },
                            channelErrorDef {
                                id,
                                title,
                                name,
                                description,
                                resolvableBy,
                                regexps,
                                fieldNames,
                                type,
                                resolution,
                            },
                            comment,
                            resolvableBy,
                            firstTransferAt,
                            firstTransfer {
                                id,
                                channel_id,
                                organisation_id,
                                createdAt,
                            },
                            lastTransferFetches(first: 200) {
                                edges {
                                    node {
                                        id,
                                        channel_id,
                                        channelDef {
                                            id,
                                            icon,
                                            name,
                                        },
                                        name,
                                        organisation_id,
                                        createdAt,
                                        duration,
                                        success,
                                        errorType,
                                        error,
                                        actionDef {
                                            id,
                                            name,
                                        },
                                        sourceDef {
                                            id,
                                            name,
                                        },
                                        flow {
                                            id,
                                            name,
                                        },
                                        flowDef {
                                            id,
                                            name,
                                        },
                                        flowStepDef {
                                            id,
                                            name,
                                        }			
                                    }
                                }
                            },
                            lastTransferAt,
                            lastTransfer {
                                id,
                                channel_id,
                                organisation_id,
                                createdAt,
                            },
                            lastErrorTransferAt,
                            lastErrorTransfer {
                                id,
                                channel_id,
                                organisation_id,
                                createdAt,
                            },
                            lastSuccessTransferAt,
                            lastSuccessTransfer {
                                id,
                                channel_id,
                                organisation_id,
                                createdAt,
                            },
                            entityDefKey,
                            displayName,
                            referencedId,
                            lastError,
                            transferCount,
                            errorCount,
                            objectDate,
                            organisation {
                                id,
                                name,
                            }
                            organisation_id,
                            flowDef {
                                name,
                                id
                            },
                            flow {
                                name,
                                organisation_id,
                                id
                            },
                        }
                    }
                }
            }
        `,
    },
});
